import {
  Checkbox,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
} from '@mui/material'
import { SelectProps } from '@mui/material/Select'
import { Field } from 'formik'
import * as React from 'react'
import { IUser } from 'src/Definitions'
import { DropdownOption } from 'src/viewModels/DropdownOption'

const ITEM_HEIGHT = 48
const ITEM_PADDING_TOP = 8
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 'fit-content',
    },
  },
  anchorOrigin: {
    horizontal: 'left' as const,
    vertical: 'bottom' as const,
  },
  transformOrigin: {
    horizontal: 'left' as const,
    vertical: 'top' as const,
  },
  getContentAnchorEl: null,
}

export interface ISelectFieldProps<T> extends Omit<
  SelectProps<T>,
  'multiple' | 'native' | 'value' | 'onChange'
> {
  name: string
  label?: string
  items?: T[]
  inputId: string
  required?: boolean
  fullwidth?: boolean
  disabled?: boolean
  errorMessage?: string
  checkedItems?: string[]
  cypressLabel?: string
  childCypressLabel?: string
  getName: (item: T) => string
  getValue: (item: T) => string
  shrinkLabel?: boolean
  onChange?: (any?: any) => void
  filterItems?: (item: T) => boolean
  outlined?: boolean
  disableDuplicates?: IDuplicateFunction
  disableInactive?: boolean
  multiple?: boolean
}

interface IDuplicateFunction {
  comparator: (a: any) => any
  list: any[]
}

export default class SelectField<T> extends React.Component<ISelectFieldProps<T>> {
  public render() {
    const {
      name,
      label,
      items,
      inputId,
      disabled,
      disableDuplicates,
      disableInactive = false,
      fullWidth = false,
      required = false,
      multiple = false,
      getName,
      getValue,
      cypressLabel,
      childCypressLabel,
      onChange,
      filterItems = () => true,
      outlined,
      errorMessage,
    } = this.props
    return (
      <FormControl
        fullWidth={fullWidth}
        required={required}
        disabled={disabled}
        error={errorMessage !== undefined}
        className={this.props.className}
        variant={outlined ? 'outlined' : 'standard'}
        margin="none"
      >
        {label && (
          <InputLabel
            id={`${inputId}-label`}
            shrink={this.shouldShrinkLabel()}
            variant={outlined ? 'outlined' : 'standard'}
          >
            {label}
          </InputLabel>
        )}
        <Field name={name}>
          {({ field }: any) => (
            <Select
              {...field}
              data-cy={cypressLabel}
              label={outlined ? label : undefined}
              labelId={label ? `${inputId}-label` : undefined}
              inputProps={{ id: inputId }}
              displayEmpty
              MenuProps={MenuProps}
              multiple={multiple}
              disabled={disabled}
              variant={outlined ? 'outlined' : 'standard'}
              onChange={(e) => {
                field.onChange(e)
                if (onChange) {
                  onChange(e)
                }
              }}
              renderValue={multiple ? this.renderText(items || []) : undefined}
            >
              {items &&
                items.filter(filterItems).map((item: T, index: number) => {
                  const itemValue = getValue(item)
                  const isChecked = Array.isArray(field.value)
                    ? field.value.includes(itemValue)
                    : field.value === itemValue
                  return (
                    <MenuItem
                      key={index}
                      value={itemValue}
                      data-cy={childCypressLabel}
                      disabled={
                        (disableDuplicates &&
                          disableDuplicates.list &&
                          disableDuplicates.list.find(
                            (x) => disableDuplicates.comparator(x) === itemValue
                          ) !== undefined) ||
                        (disableInactive &&
                          !(item as unknown as IUser).isActive &&
                          !(item as unknown as DropdownOption).isEnabled)
                      }
                    >
                      {multiple && <Checkbox checked={isChecked} />}
                      {getName(item)}
                    </MenuItem>
                  )
                })}
            </Select>
          )}
        </Field>
        {errorMessage && <FormHelperText>{errorMessage}</FormHelperText>}
      </FormControl>
    )
  }
  private renderText = (items: T[]) => (x: string[]) =>
    items
      .filter((i) => x.includes(this.props.getValue(i)))
      .map((i) => this.props.getName(i))
      .join(', ')

  private shouldShrinkLabel() {
    return this.props.shrinkLabel !== false
  }
}
